// OS ARQUIVOS DENTRO DA FALA DA NYTA (24/09/2026).
//
// Quando a Nyta fala de um arquivo do perfil, ela escreve, numa linha só, `[[arquivo:<caminho>]]`
// com o caminho que recebeu no contexto. A tela troca essa linha por um cartão do arquivo: a
// imagem, o player do áudio, o vídeo, ou o documento com o botão de abrir.
//
// O marcador leva o CAMINHO, e não um apelido curto, porque a conversa fica guardada: um "a3"
// que valia na resposta de ontem apontaria para outro arquivo hoje. O caminho é o mesmo que a
// tela de Arquivos usa, e quem resolve é o cliente, com a sessão de quem está lendo. Quem não
// pode ver o arquivo não vê o cartão.

import { trechosComTabela, type TabelaDaFala } from './tabelasNaFala';

export type ParteDaFala =
  | { tipo: 'texto'; texto: string }
  | { tipo: 'arquivo'; caminho: string }
  | { tipo: 'grafico'; id: string }
  | { tipo: 'tabela'; tabela: TabelaDaFala };

// O gráfico (`[[grafico:<id>]]`) aponta para um anexo da própria mensagem (`anexos[id]`), que o
// servidor calculou: ver `nucleo/graficoDaConversa`.
const MARCADOR = /\[\[(arquivo|grafico):\s*([^\]\s]+)\s*\]\]/g;

/**
 * A fala partida em texto, arquivos, gráficos e tabelas, na ordem em que aparecem. Texto vazio
 * entre dois cartões some. As tabelas saem do texto aqui (ver `nucleo/tabelasNaFala`).
 */
export function partesDaFala(texto: string): ParteDaFala[] {
  return partesSemTabela(texto).flatMap((parte): ParteDaFala[] => (parte.tipo !== 'texto'
    ? [parte]
    : trechosComTabela(semIdsInternos(parte.texto)).map((t) => (t.tipo === 'tabela' ? { tipo: 'tabela', tabela: t.tabela } : { tipo: 'texto', texto: t.texto }))));
}

/**
 * O id interno não aparece na fala da Nyta, nem quando o modelo desobedece ao prompt (visto ao vivo
 * em 04/10/2026, no QA da Nyta). Só fora dos marcadores, que já foram separados antes, e nunca
 * dentro de um endereço (uuid depois de "/"). A edge faz a mesma limpeza ao gravar.
 */
export function semIdsInternos(texto: string): string {
  return texto
    .replace(/\s*\[id:[^\]]*\]/gi, '')
    .replace(/([/:])?\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, (m, antes) => (antes ? m : ''));
}

function partesSemTabela(texto: string): ParteDaFala[] {
  if (!texto || !/\[\[(arquivo|grafico):/.test(texto)) return texto ? [{ tipo: 'texto', texto }] : [];
  const partes: ParteDaFala[] = [];
  let ultimo = 0;
  // `exec` em laço, e não `matchAll`: o build da web compila para um alvo que não itera RegExp.
  const marcador = new RegExp(MARCADOR.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = marcador.exec(texto)) !== null) {
    const antes = texto.slice(ultimo, m.index).trim();
    if (antes) partes.push({ tipo: 'texto', texto: antes });
    // O mesmo arquivo (ou gráfico) duas vezes seguidas vira um cartão só.
    const anterior = partes[partes.length - 1];
    const parte: ParteDaFala = m[1] === 'grafico' ? { tipo: 'grafico', id: m[2] } : { tipo: 'arquivo', caminho: m[2] };
    const repetida = anterior && anterior.tipo === parte.tipo
      && (parte.tipo === 'grafico' ? (anterior as { id: string }).id === parte.id : (anterior as { caminho: string }).caminho === (parte as { caminho: string }).caminho);
    if (!repetida) partes.push(parte);
    ultimo = (m.index ?? 0) + m[0].length;
  }
  const resto = texto.slice(ultimo).trim();
  if (resto) partes.push({ tipo: 'texto', texto: resto });
  return partes;
}

/** O marcador ainda sendo escrito no fim do streaming (`[[arqu`, `[[grafico:g`): não se mostra cru. */
export const semMarcadorIncompleto = (texto: string): string => texto
  .replace(/\[\[(?:a(?:r(?:q(?:u(?:i(?:v(?:o(?::[^\]]*)?)?)?)?)?)?)?)?\]?$/, '')
  .replace(/\[\[(?:g(?:r(?:a(?:f(?:i(?:c(?:o(?::[^\]]*)?)?)?)?)?)?)?)?\]?$/, '');
